import { countWithNoun, type GroupOperationNounForms, type IGroupOperationItem } from '@srm/ui/group-operation-dialog';
import type { INotification } from '@srm/ui/notifications';
import { IGroupActionItemResult, IGroupActionPlan } from '@models/actions/IGroupActionPlan';

/** Предпросмотр пропускается, если всё проходит и выбрано немного: итог покажется только при ошибках. */
export const SKIP_PREVIEW_MAX = 5;

/** Предел выбора групповой операции — тот же, что у сервера (`GroupActionPlanning.MaxGroupItems`). */
export const GROUP_OPERATION_MAX_ITEMS = 5000;

export const isTooManyForGroupOperation = (count: number) => count > GROUP_OPERATION_MAX_ITEMS;

export const tooManyItemsText = (forms: GroupOperationNounForms) =>
    `Выберите не больше ${countWithNoun(GROUP_OPERATION_MAX_ITEMS, forms)} — столько обрабатывает одна операция.`;

/** Ключи строк списка по ид объекта плана (у задач ключ строки может быть составным). */
export const planKeyMap = (rowKeys: string[], toPlanId: (rowKey: string) => string): Map<string, string[]> => {
    const map = new Map<string, string[]>();
    rowKeys.forEach((key) => {
        const id = toPlanId(key);
        map.set(id, [...(map.get(id) ?? []), key]);
    });
    return map;
};

export const shouldSkipPreview = (plan: IGroupActionPlan) =>
    plan.total <= SKIP_PREVIEW_MAX && plan.okCount === plan.total && plan.groupErrors.length === 0;

export const previewItems = (plan: IGroupActionPlan): IGroupOperationItem[] =>
    plan.items.map((item) => ({
        id: item.id,
        title: item.title,
        status: item.status,
        messages: item.reasons.map((reason) => reason.message),
    }));

/** Ид объектов, которые выполнятся (ok и warning). */
export const runnableIds = (plan: IGroupActionPlan) =>
    plan.items.filter((item) => item.status !== 'blocked').map((item) => item.id);

/**
 * Итог: строки запуска — по ответу сервера (запущенный без ответа — неудача); не запускавшиеся (нельзя по
 * плану) — как были.
 */
export const resultItems = (
    plan: IGroupActionPlan,
    runIds: string[],
    results: IGroupActionItemResult[],
): IGroupOperationItem[] => {
    const byId = new Map(results.map((result) => [result.id, result]));
    const run = new Set(runIds);
    return plan.items.map((item) => {
        if (!run.has(item.id)) {
            return {
                id: item.id,
                title: item.title,
                status: 'blocked',
                messages: item.reasons.map((reason) => reason.message),
            };
        }
        const result = byId.get(item.id);
        if (!result) return { id: item.id, title: item.title, status: 'failed' };
        return {
            id: item.id,
            title: item.title,
            status: result.success ? 'success' : 'failed',
            messages: result.message ? [result.message] : undefined,
            traceId: result.success ? undefined : result.traceId,
        };
    });
};

export const failedIds = (items: IGroupOperationItem[]) =>
    items.filter((item) => item.status === 'failed').map((item) => item.id);

const formatCount = (count: number) => count.toLocaleString('ru-RU');

/**
 * Короткий итог для тоста: заголовок — счёт выполненного, текст — только при неудачах; тон — как у итога окна
 * (всё выполнено — успех, ничего — ошибка, часть — внимание).
 */
export const resultToast = (actionName: string, items: IGroupOperationItem[]): INotification => {
    const run = items.filter((item) => item.status === 'success' || item.status === 'failed');
    const done = run.filter((item) => item.status === 'success').length;
    const failed = run.length - done;
    return {
        title: `${actionName}: выполнено ${formatCount(done)} из ${formatCount(run.length)}`,
        message: failed > 0 ? `Не выполнено ${formatCount(failed)} — причины в окне операции.` : undefined,
        variant: failed === 0 ? 'green' : done === 0 ? 'red' : 'yellow',
    };
};
